(() => {
  const COPY_BUBBLE_MS = 2000;
  const COUPON_WAIT_MS = 8000;
  const SUCCESS_TEXT = /thanks|success|you.?re in|subscribed|copy your code/i;
  const PLACEHOLDER_COUPON = /^(loadingcoupon|loading.?coupon|copycouponcode|\[uniquecode\])$/i;
  const instances = new Set();
  const couponListeners = new Set();
  let lastKlaviyoCoupon = "";

  const trackSubscribe = (status, emailValue) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event_parameters: null });
    window.dataLayer.push({
      event: "ga4Event",
      event_name: "subscribe",
      event_parameters: {
        module: "strava_2026_subscription",
        status: status,
        email: emailValue || ""
      }
    });
  };

  const getKlaviyoFormRoot = (root) =>
    root?.querySelector?.('[class^="klaviyo-form-"]') || null;

  const getKlaviyoFormId = (root, form) => {
    const fromData = root?.getAttribute?.("data-klaviyo-form-id");
    if (fromData) return fromData;

    const className = [...(form?.classList || [])].find((name) =>
      name.startsWith("klaviyo-form-")
    );
    return className ? className.slice("klaviyo-form-".length) : "";
  };

  const normalizeCoupon = (code) => (code || "").replace(/\s+/g, "").trim();

  const isRealCoupon = (code) => {
    const clean = normalizeCoupon(code);
    if (!clean || clean.length < 6) return "";
    if (PLACEHOLDER_COUPON.test(clean)) return "";
    return clean;
  };

  const parseCouponPayload = (payload) => {
    if (!payload) return "";
    if (typeof payload === "string") {
      try {
        payload = JSON.parse(payload);
      } catch (error) {
        return isRealCoupon(payload);
      }
    }
    return isRealCoupon(payload.code || payload.coupon || payload.coupon_code || "");
  };

  const isKlaviyoCouponUrl = (url) =>
    typeof url === "string" && /klaviyo\.com/i.test(url) && /coupon-code/i.test(url);

  const notifyCoupon = (code) => {
    const clean = isRealCoupon(code);
    if (!clean) return;
    lastKlaviyoCoupon = clean;
    couponListeners.forEach((fn) => fn(clean));
  };

  const hookKlaviyoCouponApi = () => {
    if (window.__s26HeroCouponHooked) return;
    window.__s26HeroCouponHooked = true;

    if (typeof window.fetch === "function") {
      const originalFetch = window.fetch.bind(window);
      window.fetch = function (...args) {
        const url = typeof args[0] === "string" ? args[0] : args[0]?.url;
        const request = originalFetch(...args);
        if (!isKlaviyoCouponUrl(String(url || ""))) return request;
        return request.then((response) => {
          response
            .clone()
            .json()
            .then((data) => notifyCoupon(parseCouponPayload(data)))
            .catch(() => {});
          return response;
        });
      };
    }

    const originalOpen = XMLHttpRequest.prototype.open;
    const originalSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function (method, url, ...rest) {
      this.__s26CouponUrl = url;
      return originalOpen.call(this, method, url, ...rest);
    };
    XMLHttpRequest.prototype.send = function (...args) {
      if (isKlaviyoCouponUrl(String(this.__s26CouponUrl || ""))) {
        this.addEventListener("load", () => notifyCoupon(parseCouponPayload(this.responseText)));
      }
      return originalSend.apply(this, args);
    };
  };

  hookKlaviyoCouponApi();

  const looksLikeSuccess = (form) => {
    if (!form) return false;
    if (form.querySelector('[data-testid="form-success"], .klaviyo-form-success')) {
      return true;
    }
    const heading = form.querySelector("h1 span, h1, [data-testid='form-component'] h1");
    const text = heading?.textContent?.trim() || "";
    return Boolean(text) && SUCCESS_TEXT.test(text);
  };

  const setup = (root) => {
    if (root._s26HeroAbort) {
      root._s26HeroAbort.abort();
      instances.delete(root._s26HeroApi);
    }

    const abort = new AbortController();
    const { signal } = abort;
    root._s26HeroAbort = abort;

    const formWrap = root.querySelector("[data-s26-hero-form]");
    const form = getKlaviyoFormRoot(root);
    const klaviyoFormId = getKlaviyoFormId(root, form);
    const success = root.querySelector("[data-s26-hero-success]");
    const error = root.querySelector("[data-s26-hero-error]");
    const copyButton = root.querySelector("[data-s26-hero-copy]");
    const copyBubble = root.querySelector("[data-s26-hero-copy-bubble]");
    const useCode = root.querySelector("[data-s26-hero-use-code]");
    const codeRow = root.querySelector("[data-s26-hero-code-row]");
    const infoButton = root.querySelector("[data-s26-hero-info]");
    const infoWrap = infoButton?.closest(".s26-hero__info");
    const errorText =
      root.getAttribute("data-error-text") || "Something went wrong. Please try again.";
    const couponErrorText =
      root.getAttribute("data-coupon-error-text") ||
      "We couldn’t get your discount code. Please try again.";
    const previewState = root.getAttribute("data-preview-state") || "";

    let hasCompleted = false;
    let couponWaitStarted = false;
    let copyBubbleTimer = 0;
    let pendingEmail = "";

    const hideError = () => {
      if (!error) return;
      error.hidden = true;
      error.textContent = "";
    };

    const showError = (message) => {
      if (!error) return;
      error.textContent = message || errorText;
      error.hidden = false;
    };

    const setSubmitting = (locked) => {
      root.classList.toggle("is-submitting", locked);
      const button = form?.querySelector("button");
      if (!button) return;
      if (!locked) {
        button.disabled = false;
        button.removeAttribute("aria-busy");
      }
    };

    const isConsentChecked = () =>
      Boolean(form?.querySelector('input[type="checkbox"]')?.checked);

    const readKlaviyoCoupon = () => {
      if (lastKlaviyoCoupon) return lastKlaviyoCoupon;

      const scopes = [form, root, document];
      for (const scope of scopes) {
        const node = scope?.querySelector?.('[aria-label="Copy coupon code"]');
        const text = isRealCoupon(node?.textContent);
        if (text) return text;
      }
      return "";
    };

    const applyDiscountCode = (code) => {
      const clean = isRealCoupon(code);
      if (!clean) return false;

      const label = root.querySelector("[data-s26-hero-code]");
      if (label) label.textContent = clean;
      root.setAttribute("data-discount-code", clean);
      return true;
    };

    const onCoupon = (code) => {
      if (!applyDiscountCode(code)) return;
      if (!hasCompleted) completeFromSubmit(pendingEmail);
    };
    couponListeners.add(onCoupon);
    signal.addEventListener("abort", () => couponListeners.delete(onCoupon));

    const currentDiscountCode = () =>
      isRealCoupon(
        root.querySelector("[data-s26-hero-code]")?.textContent ||
          root.getAttribute("data-discount-code")
      );

    const applyCartDiscount = (code) => {
      const clean = isRealCoupon(code);
      if (!clean) return;
      fetch(`/discount/${encodeURIComponent(clean)}`, { credentials: "same-origin" }).catch(
        () => {}
      );
    };

    const getProductsTarget = () => {
      const anchor = (root.getAttribute("data-products-anchor") || "strava-2026-products").replace(
        /^#/,
        ""
      );
      const node = document.getElementById(anchor) || document.querySelector("[data-s26-products]");
      return node?.closest?.(".shopify-section") || node;
    };

    const scrollToProducts = () => {
      const target = getProductsTarget();
      if (!target) return false;
      const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      const top = target.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({ top: Math.max(0, top), behavior: reduce ? "auto" : "smooth" });
      return true;
    };

    const showComplete = () => {
      hasCompleted = true;
      setSubmitting(false);
      hideError();
      root.classList.add("is-complete");
      root.classList.remove("is-jump", "is-submitting");
      if (form) {
        form.hidden = true;
        form.setAttribute("aria-hidden", "true");
      }
      if (success) {
        success.hidden = false;
        success.removeAttribute("hidden");
      }
      if (codeRow) {
        codeRow.hidden = false;
        codeRow.removeAttribute("hidden");
      }
      if (useCode) useCode.hidden = true;
    };

    const showJump = () => {
      hasCompleted = true;
      setSubmitting(false);
      hideError();
      root.classList.add("is-complete", "is-jump");
      root.classList.remove("is-submitting");
      if (form) {
        form.hidden = true;
        form.setAttribute("aria-hidden", "true");
      }
      if (success) {
        success.hidden = false;
        success.removeAttribute("hidden");
      }
      if (codeRow) {
        codeRow.hidden = false;
        codeRow.removeAttribute("hidden");
      }
      if (useCode) {
        useCode.hidden = false;
        useCode.removeAttribute("hidden");
      }
    };

    const showIdle = () => {
      hasCompleted = false;
      couponWaitStarted = false;
      setSubmitting(false);
      root.classList.remove("is-complete", "is-jump", "is-submitting");
      if (form) {
        form.hidden = false;
        form.removeAttribute("aria-hidden");
      }
      if (success) success.hidden = true;
      if (codeRow) codeRow.hidden = false;
      if (useCode) useCode.hidden = true;
    };

    const hideCopyBubble = () => {
      if (copyBubbleTimer) {
        window.clearTimeout(copyBubbleTimer);
        copyBubbleTimer = 0;
      }
      if (copyBubble) copyBubble.hidden = true;
    };

    const showCopyBubble = () => {
      showJump();
      if (!copyBubble) return;
      copyBubble.hidden = false;
      copyBubble.removeAttribute("hidden");
      if (copyBubbleTimer) window.clearTimeout(copyBubbleTimer);
      copyBubbleTimer = window.setTimeout(hideCopyBubble, COPY_BUBBLE_MS);
    };

    const completeFromSubmit = (emailValue) => {
      if (!applyDiscountCode(readKlaviyoCoupon())) {
        failSubmit(emailValue, couponErrorText);
        return;
      }
      if (!hasCompleted) trackSubscribe("success", emailValue);
      showComplete();
    };

    const waitForCouponThenComplete = (emailValue) => {
      if (emailValue) pendingEmail = emailValue;
      if (hasCompleted) {
        applyDiscountCode(readKlaviyoCoupon());
        return;
      }

      if (applyDiscountCode(readKlaviyoCoupon())) {
        completeFromSubmit(emailValue);
        return;
      }

      if (couponWaitStarted) return;
      couponWaitStarted = true;

      const startedAt = Date.now();
      const observer = new MutationObserver(() => {
        if (!applyDiscountCode(readKlaviyoCoupon())) return;
        observer.disconnect();
        window.clearInterval(timer);
        completeFromSubmit(emailValue);
      });
      if (form) {
        observer.observe(form, { childList: true, subtree: true, characterData: true });
      }

      const timer = window.setInterval(() => {
        if (applyDiscountCode(readKlaviyoCoupon())) {
          observer.disconnect();
          window.clearInterval(timer);
          completeFromSubmit(emailValue);
          return;
        }
        if (Date.now() - startedAt < COUPON_WAIT_MS) return;
        observer.disconnect();
        window.clearInterval(timer);
        if (!hasCompleted) failSubmit(emailValue, couponErrorText);
      }, 150);

      signal.addEventListener("abort", () => {
        observer.disconnect();
        window.clearInterval(timer);
      });
    };

    const failSubmit = (emailValue, message) => {
      if (hasCompleted) return;
      showIdle();
      showError(message || errorText);
      trackSubscribe("fail", emailValue);
    };

    const copyDiscountCode = async () => {
      const code = currentDiscountCode();
      if (!code) return;
      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(code);
        } else {
          const input = document.createElement("textarea");
          input.value = code;
          input.setAttribute("readonly", "");
          input.style.position = "absolute";
          input.style.left = "-9999px";
          document.body.appendChild(input);
          input.select();
          document.execCommand("copy");
          document.body.removeChild(input);
        }
        applyCartDiscount(code);
        showCopyBubble();
      } catch (error) {
        console.error("Strava 2026 hero copy error:", error);
      }
    };

    const setInfoOpen = (open) => {
      if (!infoWrap || !infoButton) return;
      infoWrap.classList.toggle("is-open", open);
      infoButton.setAttribute("aria-expanded", open ? "true" : "false");
    };

    if (copyButton) {
      copyButton.addEventListener("click", copyDiscountCode, { signal });
    }

    if (useCode) {
      useCode.addEventListener(
        "click",
        (event) => {
          applyCartDiscount(currentDiscountCode());
          if (!scrollToProducts()) return;
          event.preventDefault();
        },
        { signal }
      );
    }

    signal.addEventListener("abort", hideCopyBubble);

    if (infoButton && infoWrap) {
      infoButton.addEventListener(
        "click",
        (event) => {
          event.preventDefault();
          setInfoOpen(!infoWrap.classList.contains("is-open"));
        },
        { signal }
      );
      document.addEventListener(
        "click",
        (event) => {
          if (!infoWrap.contains(event.target)) setInfoOpen(false);
        },
        { signal }
      );
      document.addEventListener(
        "keydown",
        (event) => {
          if (event.key === "Escape") setInfoOpen(false);
        },
        { signal }
      );
    }

    if (form) {
      form.addEventListener(
        "click",
        (event) => {
          if (!event.target.closest("button") || hasCompleted) return;
          hideError();
          if (!isConsentChecked()) {
            setSubmitting(false);
            return;
          }
          setSubmitting(true);
          window.setTimeout(() => {
            if (hasCompleted) return;
            if (form.querySelector('input:not([type="checkbox"])')) {
              setSubmitting(false);
            }
          }, 1200);
        },
        { signal, capture: true }
      );

      form.addEventListener(
        "change",
        (event) => {
          if (event.target.matches?.('input[type="checkbox"]')) {
            setSubmitting(false);
          }
        },
        { signal }
      );

      if (!looksLikeSuccess(form)) {
        const observer = new MutationObserver(() => {
          if (hasCompleted) {
            observer.disconnect();
            return;
          }
          if (looksLikeSuccess(form)) {
            observer.disconnect();
            waitForCouponThenComplete("");
          }
        });
        observer.observe(form, { childList: true, subtree: true, characterData: true });
        signal.addEventListener("abort", () => observer.disconnect());
      }
    }

    const restoreState = () => {
      if (previewState === "submitting") {
        showIdle();
        setSubmitting(true);
        return;
      }
      if (previewState === "complete") {
        showComplete();
        return;
      }
      if (previewState === "copied") {
        showComplete();
        if (copyBubble) {
          copyBubble.hidden = false;
          copyBubble.removeAttribute("hidden");
        }
        return;
      }
      if (previewState === "jump") {
        showJump();
        return;
      }

      showIdle();
    };

    restoreState();

    const api = {
      root,
      formId: klaviyoFormId,
      handleEmailSubmit: (email) => waitForCouponThenComplete(email),
      handleEmailFail: (email) => failSubmit(email)
    };

    root._s26HeroApi = api;
    instances.add(api);
    root.dataset.initialized = "true";
  };

  const init = (scope = document) => {
    if (scope.matches?.("[data-strava-2026-hero]")) {
      setup(scope);
    }
    scope.querySelectorAll?.("[data-strava-2026-hero]").forEach(setup);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => init());
  } else {
    init();
  }

  document.addEventListener("shopify:section:load", (event) => init(event.target));

  if (!window.__s26HeroKlaviyoFormsBound) {
    window.__s26HeroKlaviyoFormsBound = true;
    window.addEventListener("klaviyoForms", (event) => {
      const detail = event.detail || {};
      const email = detail.metaData?.$email || "";
      const matched = [];

      instances.forEach((api) => {
        if (!api?.root || !document.contains(api.root)) {
          instances.delete(api);
          return;
        }
        if (detail.formId && api.formId && detail.formId !== api.formId) return;
        matched.push(api);
      });

      if (!matched.length) return;

      if (detail.type === "submit" || detail.type === "stepSubmit") {
        matched.forEach((api) => api.handleEmailSubmit(email));
        return;
      }

      if (detail.type === "failedSubmit") {
        matched.forEach((api) => api.handleEmailFail(email));
      }
    });
  }
})();
